/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation, untranslated } from "@i18n";
import { renderUserTag } from "@CubeComponents/html/userTagDom";
import React, { useRef, useState, useEffect, forwardRef, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { RootState } from "@Reducers/index";
import { convertDate } from "@Services/luxonHelper";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import IconComponent from "@CubeComponents/icons";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { TemplateUrl } from "@Services/dataManager";
import { getTemplateTypes, saveTemplate, editTemplate, deleteTemplate, archiveTemplate } from "@Actions/Forms";
import { useNavigate } from "react-router-dom";
import { formTemplateSource } from "../constants";
import { formCategories } from "@Helpers";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import { cloneTemplateSections } from "../templateClone";
import { getFormsTemplateUrl } from "@Services/utils/helpers";
import ProjectTemplateConfig from "./templateForConfiguration";
import FormTemplatePreviewPopup from "@Features/projects/components/ProjectSetings/Configuration/FormTemplatePreviewPopup";
import { projectTemplateConfigHandler } from "@Actions/projectSetting";
import _ from "lodash";
import SlickGrid1, {
  typedColumns,
  type GridClickEvent,
  type GridRowBase,
  type SlickGrid1Ref,
  type ToolbarItem,
} from "@CubeComponents/html/SlickGrid1";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import Text from "@CubeComponents/html/Text/Text";
import { objectIdString, toObjectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";
import type { FormTemplateField, SaveTemplateRequest, TemplateDefaultRequest } from "@ServerTypes/forms/forms.schema";
import type { TranslatedString } from "@Helpers/types/i18n";

/** Row type for form template data */
export interface FormTemplateRow extends GridRowBase {
  name?: string;
  description?: string;
  type?: { _id?: string; name?: string; description?: string };
  createdDate?: number;
  /** `"0"` is the system-generated sentinel stored in place of an account id. */
  createdBy?: { _id?: ObjectIdString | "0"; fullName?: string };
  /** Set on a duplicated template; drives the "(n)" suffix of the copy's name. */
  noOfDuplicatesOfthisForm?: number;
  isDefault?: boolean;
  isArchived?: boolean;
  template?: FormTemplateField[];
  lookUpUsed?: SaveTemplateRequest["data"]["lookUpUsed"];
  /**
   * Dotted paths SlickGrid reads at runtime, declared as escape-hatch keys alongside the real nested
   * objects so `typedColumns` accepts the column `field` (see useContracts.tsx for the same pattern).
   */
  "type.name"?: string;
  "createdBy.fullName"?: string;
}

/** The form-type options the create/edit modal lists; the dropdown reads `name` and `_id`. */
type FormTypeOption = { _id?: string; name?: string; description?: string };

/**
 * Narrow one `getTemplateTypes` row to the three fields the modal carries.
 *
 * `getTemplateTypesResponse.result` is `z.array(z.unknown())` on purpose — its element is the
 * `templateTypeDocument` modelled in `forms/forms.collection.ts`, which `forms.schema.ts` cannot
 * import back without a module cycle — so the row is checked here instead of assumed. Every stored
 * row satisfies this (cube-qa `Forms_types`: 51477 docs, `_id` an ObjectId and `name` a string on
 * all of them, `description` a string on 51382 and absent on the other 95), so no selectable type
 * is filtered out; the guard only keeps a malformed row from being bound as an option.
 */
const isFormTypeOption = (row: unknown): row is FormTypeOption =>
  typeof row === "object" &&
  row !== null &&
  (!("_id" in row) || typeof row._id === "string") &&
  (!("name" in row) || typeof row.name === "string") &&
  (!("description" in row) || typeof row.description === "string");

/**
 * The payload SlickGrid1 dispatches to `onSelectedRowIdsChanged`: a
 * `CustomEvent<{ args: OnSelectedRowIdsChangedEventArgs }>`, of which only `args.rows` is read here.
 * The prop is declared as taking an untyped args bag (SlickGrid1 asserts the event into it on the
 * way out), so the shape is checked here rather than assumed at the handler's signature.
 */
const isRowSelectionEvent = (
  args: Record<string, unknown>,
): args is Record<string, unknown> & { detail: { args: { rows: unknown[] } } } => {
  const detail = args.detail;
  if (typeof detail !== "object" || detail === null || !("args" in detail)) {
    return false;
  }
  const inner = detail.args;
  return typeof inner === "object" && inner !== null && "rows" in inner && Array.isArray(inner.rows);
};

/**
 * The modal's form-type value as both request schemas' `data.type` needs it: `_id` is mandatory
 * there, while a listed option carries it optionally (`getTemplateTypes` returns `z.unknown()` rows,
 * narrowed by `isFormTypeOption` above). Returns undefined for the `""` "nothing picked" sentinel and
 * for a row that reached the dropdown without an `_id`; `description` is dropped because
 * `templateTypeSchema` does not declare it, so the server strips it from the request body anyway.
 */
const toTemplateTypePayload = (
  type: "" | FormTypeOption | undefined,
): SaveTemplateRequest["data"]["type"] | undefined =>
  !type || type._id === undefined ? undefined : { _id: toObjectIdString(type._id), name: type.name };

/**
 * The create/edit-template modal's working values. `type` starts as `""` (nothing picked) and becomes
 * the chosen form-type object; that sentinel is exactly what the footer's disabled check tests.
 */
type FormTemplateFormValues = {
  name: string;
  description: string;
  type: "" | FormTypeOption;
  /**
   * Optional because the Edit path deliberately replaces the whole state with just
   * name/description/type — an edit sends no `template`/`lookUpUsed` (both optional on
   * `editTemplateSchema.data`), which is what `updateName: true` means.
   */
  template?: FormTemplateField[];
  lookUpUsed?: SaveTemplateRequest["data"]["lookUpUsed"];
  resourceType?: string;
};

/**
 * A toolbar entry as this grid builds them. SlickGrid1's exported `ToolbarItem` calls the label
 * `text`, but its own `ToolBar.tsx` renders `item.name` for entries like these (its private
 * `ToolBarEntry` names the key), so `name` is declared alongside the public type rather than the
 * whole array being asserted into it at the prop.
 */
type FormsToolbarItem = ToolbarItem & {
  name?: TranslatedString;
};

type FormTemplatesProps = {
  /** A `formCategories` value (INSPECTION, ASSETS, …) — the template list is scoped to it. */
  formCategory: string;
  /** Route param, so it enters from outside the type system; backend route schemas validate it. */
  projectId?: string;
  /** `formTemplateSource.portalSettings` | `formTemplateSource.projectSettings`. */
  source?: string;
  /** Grid-only mode: no toolbar, no row navigation (used inside the "select template" modal). */
  showOnlyGrid?: boolean;
  /** In "select template" mode, list only templates NOT already attached to `projectId`. */
  excludeTemplates?: boolean | string[];
  /** Lets a host modal enable its confirm button while exactly one row is selected. */
  formSelectBtnStateEnable?: (enabled: boolean) => void;
};

const FormTemplates = forwardRef<SlickGrid1Ref<FormTemplateRow>, FormTemplatesProps>((props, ref) => {
  const { t } = useTranslation();
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState<boolean>(false);
  const [showRemoveConfirmationModal, setShowRemoveConfirmationModal] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [openAddEditModal, setOpenAddEditModal] = useState<boolean>(false);
  const [showArchivedTemplates, setShowArchivedTemplates] = useState<boolean>(false);
  const [showTemplatesGrid, setShowTemplatesGrid] = useState<boolean>(false);
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const [excludeTemplates, setExcludeTemplates] = useState<string[]>([]);
  const navigate = useNavigate();
  const getProjectSettingObject = useSelector((state: RootState) => state.getProjectSettingObject);
  const portalSettingInfo = useSelector((state: RootState) => state.portalSettingInfo);
  const initialFormValues: FormTemplateFormValues = {
    name: "",
    description: "",
    type: "",
    template: [],
    lookUpUsed: [],
    resourceType: props.formCategory,
  };
  const [toolBarBtnState, setToolBarBtnState] = useState({
    delete: false,
    archive: false,
    unArchive: false,
    edit: false,
    add: true,
    remove: false,
    duplicate: false,
  });
  const [isGridReady, setIsGridReady] = useState<boolean>(false);
  const [formTypeOptions, setFormTypeOptions] = useState<FormTypeOption[]>([]);
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null);

  // Bound to the route's Zod request contract (see SlickGridProps.TQuery).
  // `params` removed: the route declares `resourceType` on `value` itself and the service reads it
  // from there (`{...payload.value}` in forms.service.ts), so a nested `params` was never read.
  const customQuery: NonNullable<TemplateDefaultRequest["value"]> = {
    resourceType: props.formCategory,
    isArchived: showArchivedTemplates,
  };
  if (props.projectId && !props.excludeTemplates) {
    customQuery.projectId = toObjectIdString(props.projectId);
  }
  if (props.excludeTemplates) {
    customQuery.where = [
      {
        isComplex: false,
        field: "projectId",
        operator: "notequal",
        ignoreCase: false,
        value: props.projectId,
      },
    ];
  }
  const [query, setQuery] = useState<NonNullable<TemplateDefaultRequest["value"]>>(customQuery);

  const [InspectionFormDataToSave, setInspectionFormDataToSave] = useState<FormTemplateFormValues>(initialFormValues);

  const tempRef = useRef<SlickGrid1Ref<FormTemplateRow> | null>(null);
  // `forwardRef` hands back an object ref, a callback ref, or null. This component shares the grid
  // handle through `dataGrid.current`, which only an object ref can carry, so a caller that passes a
  // callback ref (or none) gets the local one. The sole ref-passing caller — templateForConfiguration
  // — uses `useRef`.
  const dataGrid = typeof ref === "object" && ref !== null ? ref : tempRef;
  const dispatch = useDispatch();

  const handleFormStatusChange = () => {
    setShowArchivedTemplates((prev) => !prev);
  };

  const dropDownTemplate = () => (
    <SelectDropDown
      select={handleFormStatusChange}
      cssClass="e-caret-hide"
      bgColor="transparent"
      items={
        showArchivedTemplates
          ? [{ text: t("Show Active Template"), id: "showArchive" }]
          : [{ text: t("Show Archived Template"), id: "showActive" }]
      }
    >
      <IconComponent name="threedots" width="1.25rem" height="1.25rem" className="cursor-pointer" />
    </SelectDropDown>
  );

  const canAddFormTemplate = portalSettingInfo.formTemplates?.addFormTemplates?.enable;
  const canEditFormTemplate = portalSettingInfo.formTemplates?.editFormTemplates?.enable;
  const canDeleteFormTemplate = portalSettingInfo.formTemplates?.deleteFormTemplates?.enable;

  const toolbarOptions: FormsToolbarItem[] = [
    ...(!showArchivedTemplates
      ? [
          {
            name: t("Create"),
            prefixIcon: "add",
            id: "custom_create",
            disabled: !canAddFormTemplate,
          },
          {
            name: t("Edit"),
            prefixIcon: "editProfile",
            id: "custom_edit",
            disabled: !toolBarBtnState.edit || !canEditFormTemplate,
          },
          {
            name: t("Delete"),
            prefixIcon: "delete",
            id: "custom_delete",
            disabled: !toolBarBtnState.delete || !canDeleteFormTemplate,
          },
          {
            name: t("Duplicate"),
            prefixIcon: "copy",
            id: "custom_duplicate",
            disabled: !toolBarBtnState.duplicate || !canAddFormTemplate,
          },
          {
            name: t("Archive"),
            prefixIcon: "archive",
            id: "custom_archive",
            disabled: !toolBarBtnState.archive || !canEditFormTemplate,
          },
        ]
      : []),
    ...(showArchivedTemplates
      ? [
          {
            name: t("Un-Archive"),
            prefixIcon: "unarchive",
            id: "custom_un-archive",
            disabled: !toolBarBtnState.unArchive || !canEditFormTemplate,
          },
        ]
      : []),
    {
      id: "threeDots",
      template: dropDownTemplate,
      rightAllign: true,
    },
  ];

  const toolBarForProjectSettings: FormsToolbarItem[] = [
    {
      name: t("Add"),
      prefixIcon: "add",
      id: "custom_add",
      disabled: !toolBarBtnState.add,
    },
    {
      name: t("Remove"),
      prefixIcon: "delete",
      id: "custom_remove",
      disabled: !toolBarBtnState.remove,
    },
  ];

  useEffect(() => {
    setToolBarBtnState((prev) => ({
      ...prev,
      add: getProjectSettingObject.formTemplate?.add?.enable ?? false,
    }));
  }, [getProjectSettingObject.formTemplate]);

  const fetchFormTypes = useCallback(async () => {
    try {
      const response = await getTemplateTypes({
        value: {
          resourceType: props.formCategory,
        },
      });
      if (response.data.result) {
        setFormTypeOptions(response.data.result.filter(isFormTypeOption));
      }
    } catch (error) {
      console.error("Error fetching form types:", error);
      return Promise.reject(error);
    }
  }, [props.formCategory]);

  useEffect(() => {
    if (openAddEditModal) {
      fetchFormTypes();
    }
  }, [openAddEditModal, fetchFormTypes]);

  const handleDeleteInspectionForms = async () => {
    const selectedData = dataGrid.current!.dataView.getAllSelectedItems();
    // Every row the grid hands back came from Mongo with an `_id`.
    const InspectionFormIds = selectedData.map((formData) => toObjectIdString(formData._id!));
    setShowLoading(true);
    const data = {
      action: "delete",
      InspectionFormIds,
    };
    const res = await deleteTemplate(data);
    if (res.data.status) {
      dispatch(
        showToastNotification({
          content: t("Selected template(s)  have been deleted successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      dataGrid.current!.refresh!();
    } else {
      dispatch(
        showToastNotification({
          content: untranslated(res.data.errorMessage || SOMETHING_WENT_WRONG),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    setShowLoading(false);
    handleDeletModalClose();
  };

  const handleDeletModalClose = () => {
    setShowDeleteConfirmationModal(false);
  };

  const handleRemoveModalClose = () => {
    setShowRemoveConfirmationModal(false);
  };

  const handleRemoveTemplates = async () => {
    // "Remove" only exists under project settings, which always routes with a project id — but that
    // id is a route param, so it enters from outside the type system and `objectIdString` is the
    // boundary check its brand exists for.
    const projectObjectId = objectIdString.safeParse(props.projectId).data;
    if (!projectObjectId) {
      handleRemoveModalClose();
      return;
    }
    const selectedData = dataGrid.current?.dataView.getAllSelectedItems() ?? [];
    // Every row the grid hands back came from Mongo with an `_id` (same assumption, and same `!`, as
    // `handleDeleteInspectionForms` above). Annotating the row `any` erased the `ObjectIdString`
    // that `projectTemplateConfigHandler`'s `templateIds` requires; `toObjectIdString` re-tags it.
    const templateIds = selectedData.map((obj) => toObjectIdString(obj._id!));
    setShowLoading(true);
    const res = await projectTemplateConfigHandler({
      action: "remove",
      templateIds,
      projectId: projectObjectId,
    });
    if (res.data.status) {
      dispatch(
        showToastNotification({
          content: t("Template removed successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      if (dataGrid.current?.refresh) {
        dataGrid.current.refresh();
      }
    } else {
      dispatch(
        showToastNotification({
          content: untranslated(res.data.errorMessage || SOMETHING_WENT_WRONG),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    setShowLoading(false);
    handleRemoveModalClose();
  };

  const handleAddEditModalCLose = () => {
    setOpenAddEditModal(false);
    setEditMode(false);
    setInspectionFormDataToSave(initialFormValues);
  };

  const toolbarClickHandler = async (id: string) => {
    const selectedData = dataGrid.current?.dataView.getAllSelectedItems();
    if (id == "custom_add") {
      setShowTemplatesGrid(true);
    } else if (id == "custom_remove") {
      setShowRemoveConfirmationModal(true);
    } else if (id === "custom_create") {
      setOpenAddEditModal(true);
    } else if (id === "custom_delete") {
      setShowDeleteConfirmationModal(true);
    } else if (id === "custom_edit") {
      setInspectionFormDataToSave({
        // "custom_edit" is only enabled with exactly one row selected.
        name: selectedData![0]!.name!,
        description: selectedData![0]!.description!,
        type: selectedData![0]!.type!,
      });
      setOpenAddEditModal(true);
      setEditMode(true);
    } else if (id === "custom_duplicate") {
      // "custom_duplicate" is only enabled with exactly one row selected.
      const row = selectedData![0]!;
      // The duplicate is saved under the source row's form type, which every listed template has —
      // `saveTemplateSchema` requires one, so the server could not have stored a row without it.
      const duplicatedType = toTemplateTypePayload(row.type);
      if (!duplicatedType) {
        return;
      }
      const count = row.noOfDuplicatesOfthisForm || 0;

      // Fresh section/question ids AND the references between them re-pointed at the copy, so the
      // duplicate's formula, concatenate and table-row config addresses its own fields rather than
      // the source template's — see `cloneTemplateSections`.
      const tempTemplate = cloneTemplateSections(row.template!);
      setShowLoading(true);
      const res = await saveTemplate({
        action: "insert",
        data: {
          name: `${row.name}(${count + 1})`,
          description: `${row.description}(${count + 1})`,
          resourceType: props.formCategory,
          type: duplicatedType,
          template: tempTemplate,
          lookUpUsed: row.lookUpUsed ?? [],
        },
        parentFormId: toObjectIdString(row._id),
      });
      if (res.data.status) {
        dispatch(
          showToastNotification({
            content: t("Duplicate Row Created successfully"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      } else {
        dispatch(
          showToastNotification({
            content: untranslated(res.data.errorMessage || SOMETHING_WENT_WRONG),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
      setShowLoading(false);
      dataGrid.current!.refresh!();
    } else if (id === "custom_archive" || id === "custom_un-archive") {
      const formIds = selectedData!.map((form) => toObjectIdString(form._id!));
      setShowLoading(true);
      const res = await archiveTemplate({
        action: "archive",
        formIds,
        isArchived: id === "custom_archive",
      });
      if (res.data.status) {
        dispatch(
          showToastNotification({
            content: id === "custom_archive" ? t("Form Archived successfully") : t("Form Un-Archived successfully"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        dataGrid.current!.refresh!();
      }
      setShowLoading(false);
    }
  };

  const selectionEvent = (args: Record<string, unknown>) => {
    if (!dataGrid.current || props.showOnlyGrid || !isRowSelectionEvent(args)) {
      return;
    }
    const totalRowsSelected = args.detail.args.rows.length;
    if (props.formSelectBtnStateEnable) {
      props.formSelectBtnStateEnable(totalRowsSelected == 1);
    }
    if (totalRowsSelected == 0) {
      setToolBarBtnState({
        edit: false,
        duplicate: false,
        remove: false,
        delete: false,
        archive: false,
        unArchive: false,
        add: getProjectSettingObject.formTemplate?.add?.enable ?? false,
      });
    }
    if (totalRowsSelected == 1) {
      setToolBarBtnState({
        edit: true,
        duplicate: true,
        remove: getProjectSettingObject.formTemplate?.remove?.enable ?? false,
        delete: true,
        archive: true,
        unArchive: true,
        add: getProjectSettingObject.formTemplate?.add?.enable ?? false,
      });
    }
    if (totalRowsSelected > 1) {
      setToolBarBtnState({
        edit: false,
        duplicate: false,
        remove: getProjectSettingObject.formTemplate?.remove?.enable ?? false,
        delete: true,
        archive: true,
        unArchive: true,
        add: getProjectSettingObject.formTemplate?.add?.enable ?? false,
      });
    }
  };

  const formatCreatedDate = (
    _row: number,
    _cell: number,
    _value: number | undefined,
    _columnDef: unknown,
    dataContext: FormTemplateRow,
  ) => {
    const dateFormat = convertDate(Number(dataContext.createdDate), "dateForTooltip");
    const date = dataContext.createdDate;
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(
      <div className="flex items-center gap-3">
        <ToolTipComponent showTipPointer position="bottom" content={dateFormat}>
          <span>{convertDate(Number(date), "dateForText")}</span>
        </ToolTipComponent>
      </div>,
    );
    return container;
  };

  const recordClick = (args: GridClickEvent) => {
    const rowIndex = args.detail.args.row;
    // The vendor grid is `SlickGrid<TData = any>`, so its `getDataItem` hands every row back
    // untyped. It is reached through a narrowed handle instead (the pattern contractTaskGrid.tsx
    // already uses): the assignment is a checked one, not an assertion. The row stays nullable on
    // purpose - `getDataItem` yields `undefined` for an index the view no longer holds (a click
    // landing on a row a concurrent refresh removed).
    const grid: { getDataItem: (row: number) => FormTemplateRow | undefined } = dataGrid.current!.slickGrid;
    const clickedRow = grid.getDataItem(rowIndex);
    // The grid's `RowId` is `string | number | { toString(): string }`; every row here came from
    // Mongo with a string `_id`, and both consumers below want that string.
    const rowId = clickedRow?._id;
    const clickedRowId = rowId === undefined ? undefined : String(rowId);
    // `cell != 0` matches the guard on the sibling branch below: cell 0 is the checkbox-select
    // column, so without it ticking a row's checkbox also opened the template preview. (The old code
    // reached the preview only from the "name" column, so it never had this problem.)
    if (props.projectId && args.detail.args.cell != 0) {
      setPreviewTemplateId(clickedRowId ?? null);
    } else if (
      args.detail.args.cell != 0 &&
      !props.showOnlyGrid &&
      !showArchivedTemplates &&
      props.source != formTemplateSource.projectSettings
    ) {
      const url = getFormsTemplateUrl({
        templateId: clickedRowId,
        resourceType: props.formCategory,
      });
      navigate(url);
    }
  };

  useEffect(() => {
    if (!props.showOnlyGrid) {
      setToolBarBtnState({
        delete: false,
        archive: false,
        unArchive: false,
        edit: false,
        duplicate: false,
        remove: false,
        add: true,
      });
    }
    setQuery(customQuery);
  }, [showArchivedTemplates, props.formCategory]);

  const createdByTemplate = useCallback(
    (
      _row: number,
      _cell: number,
      _value: FormTemplateRow["createdBy.fullName"],
      _columnDef: unknown,
      dataContext: FormTemplateRow,
    ) => {
      const _id = dataContext.createdBy?._id;
      const container = document.createElement("div");
      if (_id === "0") {
        container.textContent = t("System Generated");
      } else {
        renderUserTag(container, { id: _id });
      }
      return container;
    },
    [],
  );

  const handleChange = (args: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const data = args.target.value;
    setInspectionFormDataToSave({
      ...InspectionFormDataToSave,
      [args.target.name]: data,
    });
  };

  const handleTypeChange = (newValue: FormTypeOption | FormTypeOption[] | null) => {
    // Single-select dropdown, so the array arm of SelectDropDown's signature is the multi-select
    // mode this instance does not enable.
    const selected = Array.isArray(newValue) ? newValue[0] : newValue;
    const data = selected
      ? {
          _id: selected._id,
          name: selected.name,
          description: selected.description,
        }
      : "";
    setInspectionFormDataToSave({
      ...InspectionFormDataToSave,
      type: data,
    });
  };

  const handleCreateInspectionFormClick = async () => {
    const { name, description, template, lookUpUsed } = InspectionFormDataToSave;
    // Both request schemas require `data.type` to be a form-type object, while this modal holds the
    // `""` "nothing picked yet" sentinel until one is chosen. The footer's disabled check is what
    // guarantees a real one by the time either call is made; this states that instead of asserting
    // the whole body past it.
    const type = toTemplateTypePayload(InspectionFormDataToSave.type);
    if (!type) {
      return;
    }
    setShowLoading(true);
    const res = editMode
      ? await editTemplate({
          action: "update",
          // The Edit toolbar entry is only enabled with exactly one row selected.
          _id: toObjectIdString(dataGrid.current!.dataView.getAllSelectedItems()[0]!._id!),
          updateName: true,
          // An edit deliberately sends no `resourceType` — that is what `updateName: true` means.
          data: { name, description, type, template, lookUpUsed },
        })
      : await saveTemplate({
          action: "insert",
          data: { name, description, resourceType: props.formCategory, type, template: template ?? [], lookUpUsed },
        });
    if (res.data.status) {
      const content = editMode
        ? t("template_updated", { name: InspectionFormDataToSave.name })
        : t("template_created", { name: InspectionFormDataToSave.name });
      dispatch(
        showToastNotification({
          content,
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      setInspectionFormDataToSave(initialFormValues);
      handleAddEditModalCLose();
      dataGrid.current!.refresh!();
    } else {
      dispatch(
        showToastNotification({
          content: untranslated(res.data.errorMessage || SOMETHING_WENT_WRONG),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    setShowLoading(false);
  };

  const refreshGrid = () => {
    dataGrid.current!.refresh!();
  };

  const headerContent = () => (
    <div className="  w-full flex justify-between rounded-md">
      <Text>{editMode ? t("Edit Form Template") : t("Create New Form Template")}</Text>
    </div>
  );
  const modelContent = () => (
    <div className=" flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <Text>{t("Template Name")}</Text>
        <span>
          <TextFieldComponent
            name="name"
            type="text"
            placeholder={t("Add Template Name")}
            showClearButton
            onChange={handleChange}
            value={InspectionFormDataToSave.name}
          />
        </span>
      </div>
      <div className="flex flex-col gap-2">
        <Text>{t("Template Description")}</Text>
        <span>
          <TextFieldComponent
            name="description"
            type="text"
            placeholder={t("Add Template Description")}
            showClearButton
            onChange={handleChange}
            value={InspectionFormDataToSave.description}
          />
        </span>
      </div>
      <div className="flex flex-col gap-2">
        <Text>{t("Template Type")}</Text>
        <span>
          <SelectDropDown
            onChange={handleTypeChange}
            value={InspectionFormDataToSave.type}
            buttonType={BUTTON_TYPES.BORDER_BUTTON}
            fields={{
              text: "name",
              value: "_id",
            }}
            options={formTypeOptions}
            buttonWidth="100%"
            popperWidth={400}
          />
        </span>
      </div>
    </div>
  );
  const footerContent = () => (
    <div className="flex gap-4 items-center">
      <ButtonComponent
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        loading={showLoading}
        disabled={
          InspectionFormDataToSave.name.trim() == "" ||
          InspectionFormDataToSave.description.trim() == "" ||
          InspectionFormDataToSave.type == ""
        }
        onClick={handleCreateInspectionFormClick}
      >
        <Text>{editMode ? t("Save") : t("Create")}</Text>
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleAddEditModalCLose}>
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
    </div>
  );

  const formatName = (data: number, value: unknown, a: unknown, b: unknown, args: FormTemplateRow) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(
      <div>
        <span>{args.name}</span>
        <span className={args.isDefault ? "ms-2 rounded-2xl bg-[#666a84] px-1.5 py-1 text-[0.625rem] text-white" : ""}>
          {args.isDefault ? "Default" : ""}
        </span>
      </div>,
    );
    return container;
  };

  const columnDefinitions = useMemo(
    () =>
      typedColumns<FormTemplateRow>([
        {
          field: "name",
          id: "name",
          filterType: "Text",
          name: t("Name"),
          width: 150,
          formatter: formatName,
        },
        {
          field: "description",
          id: "description",
          filterType: "Text",
          name: t("Description"),
          width: 150,
        },
        {
          field: "type.name",
          id: "type",
          filterType: "Text",
          name: t("Type"),
          formatter: (_row, _cell, _value, _col, dataContext) => dataContext.type?.name,
          _valueGetter: (row) => row.type?.name,
          width: 150,
        },
        {
          field: "createdDate",
          id: "createdDate",
          name: t("Created On"),
          minWidth: 120,
          filterType: "Date",
          formatter: formatCreatedDate,
          _excelValueGetter: (row) => (row.createdDate ? convertDate(Number(row.createdDate), "dateForTooltip") : ""),
        },
        {
          field: "createdBy.fullName",
          id: "createdBy",
          name: t("Created by"),
          minWidth: 150,
          filterType: "Text",
          formatter: createdByTemplate,
          _valueGetter: (row) => row.createdBy?.fullName,
        },
      ]),
    [createdByTemplate],
  );

  useEffect(() => {
    if (isGridReady) {
      // Every row the grid hands back came from Mongo with an `_id`; `excludeTemplates` holds them
      // as plain strings.
      const excludeTemplateIds = dataGrid.current!.dataView.getItems().map((record) => String(record._id!));
      setExcludeTemplates(excludeTemplateIds);
    }
  }, [isGridReady, dataGrid]);

  const handleGridCreated = (val: boolean) => {
    setIsGridReady(val);
  };
  const getGridComponent = () => {
    const toolbar = props.showOnlyGrid
      ? undefined
      : props.source == formTemplateSource.portalSettings
        ? toolbarOptions
        : toolBarForProjectSettings;

    return (
      <SlickGrid1
        ref={dataGrid}
        remoteDataSource={TemplateUrl}
        query={query}
        toolbar={toolbar}
        setGridReady={handleGridCreated}
        toolbarClickHandler={toolbarClickHandler}
        onSelectedRowIdsChanged={selectionEvent}
        infiniteScroll
        onClick={recordClick}
        enableFilteringOption
        enableSortingOption
        columnDefinitions={columnDefinitions}
      />
    );
  };

  const addEditModal = () => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      footerContent={footerContent()}
      width="30%"
      headerTitle={headerContent()}
      onCancel={handleAddEditModalCLose}
      isVisible={openAddEditModal}
      modelContent={modelContent()}
    />
  );

  const deletePopup = () => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      onCancel={handleDeletModalClose}
      footerContent={
        <div className="flex gap-4">
          <ButtonComponent loading={showLoading} onClick={handleDeleteInspectionForms} buttonType="gradientButton">
            <Text>{t("Confirm")}</Text>
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={handleDeletModalClose}>
            <Text>{t("Cancel")}</Text>
          </ButtonComponent>
        </div>
      }
      headerTitle={
        <div className=" w-full flex justify-between rounded-md">
          <Text>{t("Delete selected Form Templates(s)")}</Text>
        </div>
      }
      isVisible={showDeleteConfirmationModal}
      modelContent={
        <Text className="whitespace-normal">{t("Are you sure you want to delete the selected Form template(s)?")}</Text>
      }
      width="30%"
    />
  );

  const removePopup = () => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      onCancel={handleRemoveModalClose}
      footerContent={
        <div className="flex gap-4">
          <ButtonComponent loading={showLoading} onClick={handleRemoveTemplates} buttonType="gradientButton">
            <Text>{t("Confirm")}</Text>
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={handleRemoveModalClose}>
            <Text>{t("Cancel")}</Text>
          </ButtonComponent>
        </div>
      }
      headerTitle={
        <div className=" w-full flex justify-between rounded-md">
          <Text>{t("Remove selected Form Template(s)")}</Text>
        </div>
      }
      isVisible={showRemoveConfirmationModal}
      modelContent={
        <Text className="whitespace-normal">
          {t("Are you sure you want to remove the selected Form template(s) from this project?")}
        </Text>
      }
      width="30%"
    />
  );

  return (
    <div className="flex w-full h-full overflow-auto">
      {openAddEditModal && addEditModal()}
      {showDeleteConfirmationModal && deletePopup()}
      {showRemoveConfirmationModal && removePopup()}
      {previewTemplateId && (
        <FormTemplatePreviewPopup
          isVisible
          formTemplateId={previewTemplateId}
          resourceType={_.findKey(formCategories, (value: string) => value === props.formCategory)}
          onCancel={() => setPreviewTemplateId(null)}
        />
      )}
      {showTemplatesGrid && (
        <ProjectTemplateConfig
          excludeTemplates={excludeTemplates}
          refreshGrid={refreshGrid}
          setShowTemplatesGrid={setShowTemplatesGrid}
          source={props.source}
          formCategory={props.formCategory}
        />
      )}
      <div className="w-full h-full overflow-auto">{getGridComponent()}</div>
    </div>
  );
});
FormTemplates.displayName = "FormTemplates";
export default FormTemplates;
